/**
 * Injects deferred content_for_header scripts from #deferred-content-for-header.
 * Non-script nodes are flushed inline in the Liquid snippet; this file only
 * activates scripts (and script preloads) on first interaction or fallback delay.
 */
(function () {
  const CONFIG = {
    fallbackDelay: 5000,
    interactionEvents: ['pointerdown', 'keydown', 'scroll'],
  };

  const listenerOptions = {
    passive: true,
    capture: false,
  };

  const DeferredLoader = {
    activated: false,
    fallbackTimerId: null,
    shimApplied: false,
    originalDocAddEventListener: null,
    originalWinAddEventListener: null,

    logError(error) {
      if (window.Shopify && window.Shopify.designMode) {
        console.error('[DeferredLoader]', error);
      }
    },

    isScriptLink(link) {
      const asAttr = (link.getAttribute('as') || '').toLowerCase();
      const rel = (link.getAttribute('rel') || '').toLowerCase();
      return (
        asAttr === 'script' ||
        rel.includes('modulepreload') ||
        (rel.includes('preload') && asAttr === 'script')
      );
    },

    /**
     * Scripts injected after DOMContentLoaded never see that event. Bridge
     * late listeners so Shopify/app bootstraps still run.
     */
    applyReadyShim() {
      if (this.shimApplied) return;
      this.shimApplied = true;

      this.originalDocAddEventListener = Document.prototype.addEventListener;
      this.originalWinAddEventListener = Window.prototype.addEventListener;

      const fireIfReady = (type, listener, target) => {
        if (typeof listener !== 'function') return false;

        if (type === 'DOMContentLoaded' && document.readyState !== 'loading') {
          queueMicrotask(() => {
            try {
              listener.call(target, new Event('DOMContentLoaded'));
            } catch (error) {
              DeferredLoader.logError(error);
            }
          });
          return true;
        }

        if (type === 'load' && document.readyState === 'complete') {
          queueMicrotask(() => {
            try {
              listener.call(target, new Event('load'));
            } catch (error) {
              DeferredLoader.logError(error);
            }
          });
          return true;
        }

        return false;
      };

      Document.prototype.addEventListener = function (type, listener, options) {
        if (fireIfReady(type, listener, this)) return;
        return DeferredLoader.originalDocAddEventListener.call(this, type, listener, options);
      };

      Window.prototype.addEventListener = function (type, listener, options) {
        if (fireIfReady(type, listener, this)) return;
        return DeferredLoader.originalWinAddEventListener.call(this, type, listener, options);
      };
    },

    recreateScript(script) {
      const next = document.createElement('script');
      Array.from(script.attributes).forEach((attr) => {
        next.setAttribute(attr.name, attr.value);
      });
      if (script.textContent) {
        next.textContent = script.textContent;
      }
      return next;
    },

    injectNode(node) {
      try {
        if (node.tagName === 'SCRIPT') {
          document.head.appendChild(this.recreateScript(node));
          return;
        }
        document.head.appendChild(node);
      } catch (error) {
        this.logError(error);
      }
    },

    removeListeners() {
      CONFIG.interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, this.onInteraction, listenerOptions);
      });
    },

    cancelScheduledWork() {
      if (this.fallbackTimerId !== null) {
        clearTimeout(this.fallbackTimerId);
        this.fallbackTimerId = null;
      }
    },

    activate() {
      if (this.activated) return;
      this.activated = true;

      this.removeListeners();
      this.cancelScheduledWork();

      const template = document.getElementById('deferred-content-for-header');
      if (!template) {
        document.documentElement.dataset.appsLoaded = 'true';
        return;
      }

      this.applyReadyShim();

      const nodes = Array.from(template.content.childNodes);
      nodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'SCRIPT') {
          this.injectNode(node);
          return;
        }
        if (node.tagName === 'LINK' && this.isScriptLink(node)) {
          this.injectNode(node);
        }
      });

      template.remove();
      document.documentElement.dataset.appsLoaded = 'true';
    },

    onInteraction() {
      DeferredLoader.activate();
    },

    schedule() {
      this.fallbackTimerId = setTimeout(() => {
        this.activate();
      }, CONFIG.fallbackDelay);
    },

    init() {
      if (!document.getElementById('deferred-content-for-header')) return;

      CONFIG.interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, this.onInteraction, listenerOptions);
      });

      if (document.readyState === 'complete') {
        this.schedule();
      } else {
        window.addEventListener('load', () => this.schedule(), { once: true });
      }
    },
  };

  DeferredLoader.init();
})();
